import AddressAutoComplete from '@/components/ui/address-autocomplete';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import AssetService from '@/services/AssetService';
import type { AddressType } from '@/types/address';
import type { AssetMetadata, FormattedAsset } from '@/types/asset';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface AddressSearchStepProps {
  onAddressSelect: (address: AddressType) => void;
  onNext: () => void;
  isLoading: boolean;
  onAssetFound: (asset: FormattedAsset | null) => void;
}

export default function AddressSearchStep({
  onAddressSelect,
  onNext,
  isLoading,
  onAssetFound,
}: AddressSearchStepProps) {
  const [address, setAddress] = useState<AddressType>({
    address1: '',
    address2: '',
    formattedAddress: '',
    city: '',
    region: '',
    postalCode: '',
    country: '',
    lat: 0,
    lng: 0,
  });
  const [searchInput, setSearchInput] = useState('');
  const [foundAssets, setFoundAssets] = useState<FormattedAsset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<FormattedAsset | null>(
    null,
  );
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingParcel, setIsLoadingParcel] = useState(false);

  const handleAddressSelect = async (newAddress: AddressType) => {
    setAddress(newAddress);
    onAddressSelect(newAddress);
    setIsSearching(true);

    try {
      const response = await AssetService.lookupAsset(
        newAddress.formattedAddress,
      );
      const assets = response.data;
      setFoundAssets(assets);

      // If exactly one asset is found, select it automatically
      if (assets.length === 1) {
        handleAssetSelect(assets[0]);
      } else if (assets.length === 0) {
        toast.info('No property details found. You can enter them manually.');
        onAssetFound(null);
      }
    } catch (error) {
      console.error('Error looking up asset:', error);
      toast.error('Failed to lookup property details');
    } finally {
      setIsSearching(false);
    }
  };

  const handleAssetSelect = async (asset: FormattedAsset) => {
    // Round lotSizeSquareFeet to nearest integer if it exists
    const roundedAsset = {
      ...asset,
      lotSizeSquareFeet: asset.lotSizeSquareFeet
        ? Math.round(asset.lotSizeSquareFeet)
        : undefined,
    };

    setSelectedAsset(roundedAsset);

    // Only fetch parcel data if we have the required IDs
    if (asset.parcelID && asset.zpid) {
      setIsLoadingParcel(true);
      try {
        const parcelResponse = await AssetService.lookupParcel(
          asset.parcelID,
          asset.zpid,
        );
        const enrichedAsset = {
          ...roundedAsset,
          parcelData: parcelResponse as AssetMetadata,
        };
        onAssetFound(enrichedAsset);
      } catch (error) {
        console.error('Error fetching parcel details:', error);
        toast.error('Failed to fetch additional property details');
        onAssetFound(roundedAsset);
      } finally {
        setIsLoadingParcel(false);
      }
    } else {
      onAssetFound(roundedAsset);
    }
  };

  const handleNext = () => {
    if (!address) {
      toast.error('Please select an address first');
      return;
    }
    onNext();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Search Property</CardTitle>
          <CardDescription>
            Enter the property address to search for existing details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <AddressAutoComplete
            address={address}
            setAddress={handleAddressSelect}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            dialogTitle="Search Property Address"
          />

          {isSearching && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Searching for property...</span>
            </div>
          )}

          {foundAssets.length > 1 && (
            <div className="space-y-2">
              <label htmlFor="property-select" className="text-sm font-medium">
                Select Property
              </label>
              <Select
                value={selectedAsset?.parcelID || ''}
                onValueChange={(value) => {
                  const asset = foundAssets.find((a) => a.parcelID === value);
                  if (asset) {
                    handleAssetSelect(asset);
                  }
                }}
              >
                <SelectTrigger id="property-select">
                  <SelectValue>
                    {selectedAsset
                      ? selectedAsset.address.full
                      : 'Select a property'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {foundAssets.map((asset) => {
                    return (
                      <SelectItem
                        key={asset.parcelID || asset.address.full}
                        value={asset.parcelID || ''}
                      >
                        {asset.address.full}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}

          {isLoadingParcel && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Fetching property details...</span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleNext}
          disabled={isLoading || isSearching || isLoadingParcel}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading...
            </>
          ) : (
            'Next'
          )}
        </Button>
      </div>
    </div>
  );
}
